// Auto-generated from docs/architecture/shared-ui.md — do not edit manually
const content = `
# Shared UI Architecture

\`@vortx/shared-ui\` is the cross-app component library that provides the design system, hooks, services, utilities, and UI components shared by all Vortx Flux domain applications. It lives at \`apps/vortx-shared-ui/\` and is consumed as a workspace package.

## Overview

The Vortx frontend is a monorepo where \`flux-web\` is a thin shell app that composes domain-specific workspace packages (Site DNA, Flux EV, Flux PhD, Flux COLD, Flux EMS). All of these packages depend on \`@vortx/shared-ui\` for:

- **Design system** -- Vortx brand colors, typography, MUI theme creation, light/retro mode switching
- **Authentication** -- Firebase Auth provider, route guards, user profile management
- **API services** -- Centralized API client with auth token injection for all backend services
- **Hooks** -- 40+ shared React hooks covering responsive design, optimization lifecycle, scenario management, financial analysis, and UX patterns
- **Data visualization** -- 3D WebGL visualizations (Three.js), Recharts-based graphs, data export
- **Form components** -- Validated numeric inputs, form layout primitives, tariff builders
- **Schemas** -- Zod validation schemas for scenarios, tariffs, equipment, results
- **Utilities** -- Financial calculations, tariff transformations, error parsing, data formatting

## Package Structure

\`\`\`
apps/vortx-shared-ui/
  package.json              # @vortx/shared-ui, peer deps on MUI 7, React 18, Firebase, Recharts, Zustand
  src/
    index.js                # Barrel re-export of all submodules
    FinancialAnalysis.jsx   # Full-page financial analysis component (large, ~110KB)
    reoptTransform.js       # REopt results -> daily time-series transformer
    resilienceSimulation.js # 24-hour outage simulation engine
    auth/                   # Firebase authentication layer
    components/             # Shared page-level components (meters, budget, save status)
    config/                 # Centralized configuration defaults
    contexts/               # React context providers (Retro, Boot, Activity)
    core/                   # Theme system (brand colors, MUI theme factory)
    data-display/           # Charts, 3D visualizations, tables, export
    extraction/             # Document upload + AI extraction review
    forms/                  # Reusable form inputs (NumberInput, FormRow, ToggleSection)
    hooks/                  # 40+ shared hooks (responsive, optimization, scenario, financial)
    schemas/                # Zod validation schemas for all data models
    services/               # API service layer (14 service modules)
    stores/                 # Zustand global stores (resultsStore)
    tariff/                 # Tariff selection, editing, and TOU building
    theme-mode/             # Light/retro mode switching with localStorage persistence
    utils/                  # Pure utility functions (calculations, transformers, formatters)
\`\`\`

## Core -- Design System

**Location:** \`src/core/\`

The design system is the single source of truth for all visual styling across every Vortx app.

### theme.js -- Brand Colors

Exports three color objects that MUST be used for all styling (never hardcode colors):

| Export | Purpose |
|--------|---------|
| \`vortxColors\` | Primary brand palette: gold (#b28c50), green (#1f2e2b), charcoal (#2c2c2c), stone (#a6a19b), white (#f4f1ec), teal (#3f6a64), error/success/warning |
| \`retroColors\` | Dark-mode accent palette: accent (#5fd4c4), bg (#1a1a1a), surface (#242424), input (#1e1e1e), muted/dimmed grays |
| \`chartColors\` | Data visualization palette organized by domain: energy sources (solar, grid, battery, EV, HVAC), energy flows (load, export, loss), financial (cost, demand, savings), status (optimal, infeasible), topology (6-color array for Flux PhD), multi-series (8-color arrays for light and dark modes) |

Also exports:
- \`vortxTypography\` -- Font families: Syne (headings), Inter (body), JetBrains Mono (code)
- \`getComponentOverrides(mode)\` -- MUI component overrides for both light and retro modes, covering CssBaseline, Button, Card, Paper, Table, TextField, AppBar, Chip, Alert

### createVortxTheme.js -- Theme Factory

\`createVortxTheme(mode)\` creates a complete MUI theme object. Accepts \`'light'\` or \`'retro'\` (string or \`{ mode }\` object). Maps \`'retro'\` to MUI's \`'dark'\` palette mode internally. Sets up palette colors, typography, background colors, and applies all component overrides from \`getComponentOverrides()\`.

### Key Files

| File | Description |
|------|-------------|
| \`src/core/theme.js\` | Brand colors, chart colors, typography, component overrides |
| \`src/core/createVortxTheme.js\` | MUI theme factory (\`createVortxTheme\`) |
| \`src/core/index.js\` | Barrel exports for core module |

## Theme Mode -- Light/Retro Switching

**Location:** \`src/theme-mode/\`

Provides theme mode switching between light and retro (dark) modes with localStorage persistence and backwards compatibility from legacy "dark" mode naming.

| Export | Type | Description |
|--------|------|-------------|
| \`ThemeModeProvider\` | Component | Wraps the app, creates MUI theme via \`createVortxTheme\`, persists mode to localStorage |
| \`useThemeMode\` | Hook | Returns \`{ mode, isRetro, isLight, isDark, toggleMode, setMode }\` |

## Authentication

**Location:** \`src/auth/\`

Firebase Auth integration with Platform API profile management.

| Export | Type | Description |
|--------|------|-------------|
| \`AuthProvider\` | Component | Wraps the app. Accepts \`apiBaseUrl\`, \`firebaseAuth\`, and optional \`profileEndpoint\`. Manages Firebase \`onAuthStateChanged\`, fetches user profile from Platform API, caches profile in sessionStorage |
| \`useAuth\` | Hook | Returns \`{ currentUser, userProfile, loading, login, signup, logout, refreshUserProfile }\` |
| \`PrivateRoute\` | Component | Route guard: redirects to \`/login\` if unauthenticated, \`/no-access\` if no product access, \`/complete-profile\` if profile incomplete |
| \`ModuleRoute\` | Component | Route guard for module-level access control |

## Contexts

**Location:** \`src/contexts/\`

Shared React context providers used across multiple apps.

| Export | Type | Description |
|--------|------|-------------|
| \`RetroProvider\` / \`useRetroContext\` | Context | Navigation state for the retro-themed shell: current app, project, scenario context, tab state, transition animations. \`useRetroContextSafe\` returns no-ops outside the provider |
| \`BootProvider\` / \`useBootContext\` | Context | Progressive boot animation state for retro mode. Tracks subsystem readiness, panel power levels (header/left/right/footer/content), auth resolution |
| \`ActivityProvider\` / \`useActivity\` | Context | User activity tracking: writes presence heartbeats and page-view events to Firestore for admin dashboards. Tracks idle state, session ID, page navigation |

## Tariff Components

**Location:** \`src/tariff/\`

Complete tariff management UI shared by all domain apps.

| Export | Type | Description |
|--------|------|-------------|
| \`UnifiedTariffSelector\` | Component | Cascading dropdowns for Utility -> Tariff Schedule -> Option selection from the Vortx Rate Database. Uses \`useAuth\` for API calls |
| \`TariffProvider\` / \`useTariff\` | Context | Simple context holding the currently selected tariff object |
| \`CustomRateBuilder\` | Component | Full custom rate structure editor with blended, TOU, and tiered rate support |
| \`TOUBuilder\` | Component | Time-of-use period builder with visual schedule grid |
| \`TOUHeatMapGrid\` | Component | 24x12 (hours x months) heatmap visualization of TOU rate periods |
| \`TierEditor\` | Component | Tiered rate editor for volumetric energy charges |

## Data Display Components

**Location:** \`src/data-display/\`

Charts, tables, 3D visualizations, and data export components.

### 3D Visualizations (Three.js / React Three Fiber)

| Component | Description |
|-----------|-------------|
| \`DispatchRibbon3D\` | 3D stacked ribbon chart showing 24-hour energy dispatch by source (Grid, PV, BESS, Generator). Uses exploded view, orbital camera, and animated entrance. Built on \`@react-three/fiber\` and \`@react-three/drei\` |
| \`LoadProfileTerrain\` | 3D terrain mesh of 365-day x 24-hour load profile. Color gradient from green (low) to red (peak). Supports year/summer/winter range presets. NOT exported from barrel index to avoid pulling Three.js into the main bundle -- must be imported via \`React.lazy()\` with a direct path |
| \`DashboardScene3D\` | Animated waveform background for the dashboard. 5 oscillating lines using app brand colors |

### Charts and Tables (Recharts / MUI)

| Component | Description |
|-----------|-------------|
| \`PowerGraph\` | Primary dispatch visualization. Recharts \`AreaChart\` with toggleable series (solar, grid, battery, generator, unmet load). Supports dark mode, resilient mode, cursor timestamp, and PNG export |
| \`SystemDesignSummary\` | Card grid showing system sizes from optimization results (PV kW, battery kWh, generator kW) |
| \`OpExTable\` | Operating expense table showing annual energy costs, demand charges, and fuel costs |
| \`EmissionsPanel\` | CO2 emissions comparison panel (baseline vs optimized) |
| \`ExportDataTab\` | Tabbed CSV/JSON export interface for time-series and summary data |
| \`StatusBadge\` | Colored status chip (optimal, infeasible, error, running) |
| \`SectionCard\` | Styled card wrapper with title, icon, and collapsible content |
| \`ProjectTable\` | Table primitives: \`ProjectTableContainer\`, \`ProjectTableHead\`, \`ProjectTableHeaderCell\`, \`ProjectTableBody\` |

## Shared Components

**Location:** \`src/components/\`

Page-level components used across multiple domain apps.

| Component | Description |
|-----------|-------------|
| \`MetersAndTariffsSection\` | Multi-meter configuration section with tariff assignment |
| \`MeterCard\` | Individual meter configuration card (name, type, load profile, tariff) |
| \`MeterLoadProfile\` | Load profile display/editor for a single meter |
| \`LoadProfileChart\` | Simple Recharts line chart for load profile preview |
| \`DecompositionChart\` | Load decomposition stacked area chart |
| \`BudgetTab\` | Full budget/financial modeling tab (~56KB, the largest single component) |
| \`AddExistingProjectDialog\` | Dialog for adding existing projects to a user's workspace |
| \`SaveStatusIndicator\` | Auto-save status display (saved, saving, error) |
| \`NoAccess\` | Access denied page shown by \`PrivateRoute\` |

## Form Components

**Location:** \`src/forms/\`

Reusable form input primitives.

| Component | Description |
|-----------|-------------|
| \`NumberInput\` | Validated numeric input with formatting, min/max constraints, and unit label support |
| \`FormRow\` | Horizontal label + input layout row |
| \`ToggleSection\` | Collapsible section with toggle switch |

## Hooks

**Location:** \`src/hooks/\`

40+ hooks organized into functional categories. These are the workhorses of the frontend.

### Base Utility Hooks

| Hook | Description |
|------|-------------|
| \`useResponsive\` | MUI breakpoint detection: \`isMobile\`, \`isTablet\`, \`isDesktop\`, \`isLargeDesktop\`, \`breakpoint\` name. Also exports \`responsiveValue()\` and \`responsiveStyles()\` helpers |
| \`useDebounce\` | Debounces a value with configurable delay (default 500ms) |
| \`useLocalStorage\` | \`useState\` with automatic localStorage persistence |
| \`useAsync\` | Async operation state manager: \`{ execute, status, value, error }\` |
| \`useInterval\` | \`setInterval\` wrapper with automatic cleanup |
| \`useProgressiveLoad\` / \`useInfiniteScroll\` | Paginated data loading with scroll-triggered fetch |

### UX Pattern Hooks

| Hook | Description |
|------|-------------|
| \`useOptimisticAction\` / \`useOptimisticField\` | Optimistic UI updates that revert on server error |
| \`useSkeletonState\` / \`useStaggeredSkeleton\` | Loading skeleton state management with staggered reveal |
| \`useFrameCounter\` | Animation frame counter for smooth transitions |
| \`useBroadcastChannel\` | Cross-tab communication via BroadcastChannel API |
| \`useOptimisticSync\` | Full optimistic sync engine for form saves |
| \`useValidation\` | Field-level validation with error messages |
| \`useDirtyTracking\` | Tracks unsaved changes for navigation guards |

### Scenario Hooks

| Hook | Description |
|------|-------------|
| \`useScenarioForm\` | Manages the full scenario form state (all equipment, tariff, site, load fields) |
| \`useScenarioFormRHF\` | React Hook Form variant with Zod schema (\`ScenarioFormSchema\`) |
| \`useScenarioLoader\` | Loads existing scenarios from backend, initializes new scenarios, handles tariff hydration |
| \`useScenarioPayload\` | Transforms form state into solver-compatible payload for submission |
| \`useLoadProfile\` | Load profile data management (upload, preview, validation, stats) |

### Optimization Hooks

| Hook | Description |
|------|-------------|
| \`useOptimization\` | Full optimization lifecycle: submit to NREL REopt, poll status, retrieve results. Handles rate limits and smart restart detection |
| \`useOptimizationStore\` | Zustand store accessor for optimization state across components |
| \`useOptimizationSubmit\` | Submission logic extracted from useOptimization for reuse |

### Financial Hooks

| Hook | Description |
|------|-------------|
| \`useBudget\` | Budget modeling and CapEx/ITC calculations |
| \`useCapexRates\` | Equipment cost rate lookups |
| \`useCommercialAssumptions\` | Commercial project financial assumptions (discount rate, escalation, etc.) |
| \`useFinancialAnalysis\` | NPV, payback, IRR, and cashflow analysis |
| \`useSystemSizes\` | Extract system sizes from optimization results |
| \`useMonthlyPerformance\` | Monthly energy and cost performance aggregation |

### Data Hooks

| Hook | Description |
|------|-------------|
| \`useEquipmentDatabase\` | Equipment catalog lookups (PV modules, batteries, inverters) |
| \`useGraphData\` | Transform results into chart-ready data series |
| \`useLoadPreviewData\` | Preview load profile before optimization |
| \`useFormValidation\` | Form-wide validation state aggregation |

### Tracked Service Hooks

Wrappers around service calls that integrate with activity tracking.

| Hook | Description |
|------|-------------|
| \`useTrackedProjectService\` | Project CRUD with activity events |
| \`useTrackedScenarioService\` | Scenario CRUD with activity events |
| \`useTrackedOptimization\` | Optimization submission with activity events |
| \`useTrackedExport\` | Data export with activity events |

### Navigation and Sync Hooks

| Hook | Description |
|------|-------------|
| \`useHashTabNavigation\` | URL hash-based tab navigation with browser back/forward support |
| \`useTariffSync\` | Syncs tariff selection between form state and TariffContext |
| \`useDateDaySync\` | Syncs date picker and day index for daily time-series views |
| \`useSidebarSync\` | Syncs sidebar state with RetroContext navigation |

### Domain Hooks

| Hook | Description |
|------|-------------|
| \`useGeocode\` | Address to lat/lon geocoding |
| \`useCaDerDetection\` | California DER (Distributed Energy Resources) charges auto-detection |
| \`useUnifiedProjects\` | Unified project listing across all domain apps (Site DNA, Flux EV, etc.) |

## Services -- API Layer

**Location:** \`src/services/\`

Centralized API service layer consumed by all domain apps. Every service uses \`API_BASE_URL\` (from \`REACT_APP_API_URL\` env var) and Firebase auth token injection.

### Core API (api.js)

| Export | Description |
|--------|-------------|
| \`API_BASE_URL\` | Base URL from \`REACT_APP_API_URL\` env var. MUST be used for all fetch calls |
| \`getAuthHeaders(currentUser)\` | Returns headers with \`Authorization: Bearer <token>\` and \`X-Frontend-Source\` |
| \`apiRequest(url, options, currentUser)\` | Generic authenticated fetch wrapper |
| \`apiGet\` / \`apiPost\` / \`apiPut\` / \`apiPatch\` / \`apiDelete\` | HTTP method convenience functions |
| \`handleApiResponse(response)\` | Parses response, throws on error status |

### createApiClient.js

Factory function \`createApiClient(frontendSource)\` for apps that need a distinct \`X-Frontend-Source\` header identifier. Returns the same API methods but tagged per-app.

### Domain Services

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
